import { AuthType, AuthInfo, ErrorDetails } from "./interfaces";
import { Log } from "./logger";
import { PlatformDetails } from "./platform";
import { PlatformName, BrowserName, DeviceModel, DeviceType } from "./enumnames";
import { TracingManager, TracingComponent } from "./tracingmanager";
import { CancellableWait } from "./cancellablewait";
import { trace, context, propagation } from "@opentelemetry/api";
import { NetworkType } from "./types";
import { NetworkTypeEnum } from "./internaltypes";

const LOGTAG = "utils";

export function IsXbox(platform: PlatformDetails): boolean {
    return platform.os === PlatformName.XBOX;
}

export function IsEdge(platform: PlatformDetails): boolean {
    return platform.browser === BrowserName.EDGE;
}

export function IsXboxEdge(platform: PlatformDetails): boolean {
    return IsXbox(platform) && IsEdge(platform);
}

export function IsiOS(platform: PlatformDetails): boolean {
    return platform.os === PlatformName.IOS;
}

export function IsiPadOS(platform: PlatformDetails): boolean {
    return platform.os === PlatformName.IPADOS;
}

export function IsiDevice(platform: PlatformDetails): boolean {
    return IsiOS(platform) || IsiPadOS(platform);
}

export function IsVisionPro(platform: PlatformDetails): boolean {
    return platform.deviceModel === DeviceModel.VISION_PRO;
}

/**
 * Returns true if the input string is an ipv4 address format. X.X.X.X -> X:<0 - 255>
 **/
export function IsValidIPv4(ipAddress: string): boolean {
    if (
        /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(
            ipAddress
        )
    ) {
        return true;
    }
    return false;
}

/**
 * Returns an randomly generated number in string format for the requested length.
 **/
export function GetRandNumericString(length: number): string {
    let multiplier = 1;
    for (let x = 1; x < length; x++) {
        multiplier = multiplier * 10;
    }
    let randnum = "" + Math.floor(Math.random() * multiplier);
    randnum = randnum.padEnd(length, "0");
    return randnum;
}

/**
 * Convert a signed integer value to an unsigned integer value.
 *
 * Returns signed 2's complement for negative numbers and results in no-op for positive numbers.
 *
 * Unified error codes are represented as 32 bit unsigned integers.
 *  Because Javascript's number type is not a 32 bit integer, these codes can be sent as a negative value.
 * For example, the code 0xC1C8B0B0 is sent as -1043812176, or -0x3E374F50 in hex.  This code is converted
 * to 0xFFFFFFFFC1C8B0B0, which after extracting the rightmost 32 bits results in the original code, 0xC1C8B0B0.
 *
 * https://confluence.nvidia.com/display/GNCE/New+Schema+Format
 **/
export function convertToUnsignedInt(code: number): number {
    return code >>> 0;
}

/**
 * Returns an hex format of an integer code. The hex string starts with 0x and has 8 upper case hex format.
 **/
export function GetHexString(code: number): string {
    code = convertToUnsignedInt(code);
    let hex = ("00000000" + code.toString(16).toUpperCase()).slice(-8);
    return "0x" + hex;
}

/**  Returns true if origin service bits are set.
 **/
export function isUnifiedErrorCode(code: number): boolean {
    code = convertToUnsignedInt(code);
    const originService = code & 0x00ff0000;
    return originService !== 0;
}

/**
 * Returns true if the browser is Chromium-based, else false.
 * Intentionally true for Edge Chromium, modern Opera, etc.
 */
export function IsChromium(): boolean {
    var isChromium = (window as any).chrome;

    return !!isChromium;
}

/**
 * Returns true if the browser is Safari, else false.
 **/
export function IsSafari(platformDetails: PlatformDetails): boolean {
    return platformDetails.browser === BrowserName.SAFARI;
}

/**
 * Returns true if this is an iPhone or an iPod Touch.
 */
export function IsiPhone(platformDetails?: PlatformDetails): boolean {
    if (platformDetails) {
        return platformDetails.os === PlatformName.IOS;
    }
    return /iPhone|iPod/.test(navigator.userAgent);
}

/**
 * Returns true if this is an iPad.
 */
export function IsiPad(platformDetails?: PlatformDetails): boolean {
    if (platformDetails) {
        return platformDetails.os === PlatformName.IPADOS;
    }
    return /^(?!.*chrome).*safari/i.test(navigator.userAgent) && !IsiPhone() && IsTouchDevice();
}

/**
 * Returns true if this is a WebOS device.
 */
export function IsWebOS(platformDetails: PlatformDetails): boolean {
    return platformDetails.os === PlatformName.WEBOS;
}

export function IsChromeOS(platformDetails: PlatformDetails) {
    return platformDetails.os === PlatformName.CHROME_OS;
}

export function IsWindowsOS(platformDetails: PlatformDetails) {
    return platformDetails.os === PlatformName.WINDOWS;
}

export function IsMacOS(platformDetails: PlatformDetails) {
    return platformDetails.os === PlatformName.MAC;
}

export function IsTizenOS(platformDetails: PlatformDetails) {
    return platformDetails.os === PlatformName.TIZEN;
}

export function IsLinuxOS(platformDetails: PlatformDetails) {
    return platformDetails.os === PlatformName.LINUX;
}

export function IsAndroidOS(platformDetails: PlatformDetails) {
    return platformDetails.os === PlatformName.ANDROID;
}

export function IsSteamDeck(platformDetails: PlatformDetails) {
    return platformDetails.deviceModel === DeviceModel.STEAMDECK;
}

export function IsFirefox(platformDetails: PlatformDetails) {
    return platformDetails.browser === BrowserName.FIREFOX;
}

/**
 * Error codes return by utils lib
 */
export const enum UtilsErrorCode {
    NoNetwork = 0xc0f21001, // An http operation could not performed because system is not connected to internet.
    NetworkError = 0xc0f21002, // Network error while performing http request.
    AuthTokenNotUpdated = 0xc0f22001, //client was not able to provide the auth token.
    ResponseParseFailure = 0xc0f22003, // JSON parse error
    UserAborted = 0x00f22001, // Abort called during HTTP operation
    ExceptionHappened = 0xc0f21004 // Generic error due to unknown exception
}

/**
 * type to be used for header in performHttpRequest options arg.
 */
export interface HttpRequestHeaders {
    [key: string]: string;
}

export interface RequestHttpOptions {
    method?: string;
    headers: HttpRequestHeaders;
    body?: string | FormData;
    retryCount?: number;
    timeout?: number;
    backOffDelay?: number;
    keepalive?: boolean; // only used in the context of fetchAPI
}

export interface Response {
    status: number;
    data: string;
    retries: number;
}

export const DefaultHttpRequestOptions: RequestHttpOptions = {
    method: "GET",
    headers: {},
    body: "",
    retryCount: 0,
    timeout: 0,
    keepalive: false
};

function addHttpRequestAttributes(method: string, urlString: string, headers: HttpRequestHeaders) {
    const span = trace.getActiveSpan();
    if (!span) {
        return;
    }

    let httpRequestAttributes: Map<string, string> = new Map([
        ["component", TracingComponent.name],
        ["component.version", TracingComponent.version],
        ["http.method", method],
        ["http.url", urlString],
        ["type", ""],
        ["http.hostname", ""],
        ["http.path", ""]
    ]);

    try {
        const url: URL = new URL(urlString);
        httpRequestAttributes.set("type", url.protocol);
        httpRequestAttributes.set("http.hostname", url.hostname);
        httpRequestAttributes.set("http.path", url.pathname);
    } catch (err) {
        Log.e("{d988e7f}", "{997fb3a}");
    }

    for (const [key, value] of httpRequestAttributes) {
        span.setAttribute(key, value);
    }
    span.setAttributes(headers);
}

function addResultSuccessAttributes(status: number) {
    const span = trace.getActiveSpan();
    if (span) {
        span.setAttribute("error", "false");
        span.setAttribute("http.status_code", status.toString());
    }
}

function addErrorAttributes() {
    const span = trace.getActiveSpan();
    if (span) {
        span.setAttribute("error", "true");
    }
}

/**
   * Performs an asynchronous http(s) request. Returns a promise which resolves when the http request completed (irrespective of http status code).
   *         Promise will be rejected in case of network errors.
   *         resolve( { status: <http status code> , data: <http response> } )
   *         reject ( { code: <integer error code> } )
   * Call the abort method on the returned Promise to abort any pending network requests and reject the Promise. The
   * rejected object will be of the form: { code: UtilsErrorCode.UserAborted }
   * @param url- http url
   * @param options - optional http method, headers and other settings:
          {method: string;         http method - GET(default), POST, PUT, DELETE
          headers: {};             Key value pairs of headers.
          body: string | FormData; Request body for POST and PUT request.
          retryCount: number;      Number of retires in error cases.
          timeout: number;         timeout duration.
          keepalive: boolean;      Keeps the connection alive even if the tab is closed.}
  **/
export function performHttpRequest(
    url: string,
    options: RequestHttpOptions = DefaultHttpRequestOptions,
    authInfo?: AuthInfo,
    abortController?: AbortController
): Promise<Response> {
    const method = options.method ?? "GET";
    const request = () => {
        addHttpRequestAttributes(method, url, options.headers);
        return performHttpRequestImpl(url, options, authInfo, abortController);
    };
    return TracingManager.getInstance().createSpan<Response>(method, request);
}

function performHttpRequestImpl(
    url: string,
    options: RequestHttpOptions = DefaultHttpRequestOptions,
    authInfo?: AuthInfo,
    abortController?: AbortController
): Promise<Response> {
    let retryCount = options.retryCount === undefined ? 1 : options.retryCount + 1;
    const max_retry_count = retryCount;
    const backOffIncrement = options.backOffDelay ?? 500;
    let backOffTime = backOffIncrement;

    const method = options.method ?? "GET";
    let requestHeaders = new Headers();
    const controller = abortController ?? new AbortController();
    const addReqHeaders = (authInfo?: AuthInfo) => {
        if (authInfo) {
            switch (authInfo.type) {
                case AuthType.JWT_GFN:
                    if (authInfo.token) {
                        requestHeaders.set("authorization", "GFNJWT " + authInfo.token);
                    }
                    break;
                case AuthType.JWT_PARTNER:
                    if (authInfo.token) {
                        requestHeaders.set("authorization", "GFNPartnerJWT auth=" + authInfo.token);
                    }
                    break;
            }
        }
        if (!(options.headers === undefined)) {
            Object.keys(options.headers).forEach(key => {
                requestHeaders.set(key, options.headers[key]);
            });
        }
        const carrier: { [key: string]: string } = {};
        propagation.inject(context.active(), carrier);
        for (const [key, value] of Object.entries(carrier)) {
            requestHeaders.set(key, value);
        }
    };
    addReqHeaders(authInfo);

    const promise = new Promise<Response>((resolve_, reject_) => {
        const resolve = (response: Response) => {
            addResultSuccessAttributes(response.status);
            resolve_(response);
        };
        const reject = (x?: any) => {
            addErrorAttributes();
            reject_(x);
        };
        const performRequest = () => {
            const retryRequest = (errorCode: number): boolean => {
                if (retryCount > 1) {
                    retryCount--;
                    Log.d("{d988e7f}", "{97555ed}", errorCode, retryCount);
                    const wait = new CancellableWait(backOffTime, controller.signal);
                    wait.getPromise()
                        .then(performRequest)
                        .catch((err: any) => {
                            if (wait.isAborted()) {
                                reject({
                                    code: UtilsErrorCode.UserAborted,
                                    retries: max_retry_count - retryCount
                                });
                            } else {
                                // Should never occur, since we handle all the errors within performRequest.
                                Log.w("{d988e7f}", "{d3ca39d}", JSON.stringify(err));
                                reject({
                                    code: UtilsErrorCode.ExceptionHappened,
                                    retries: max_retry_count - retryCount
                                });
                            }
                        });
                    backOffTime += backOffIncrement;
                    return true;
                }
                return false;
            };
            if (!navigator.onLine) {
                reject({
                    code: UtilsErrorCode.NoNetwork,
                    description: "No network",
                    retries: max_retry_count - retryCount
                });
            } else {
                let reqInit: RequestInit = {
                    method: method,
                    headers: requestHeaders,
                    mode: "cors",
                    keepalive: options.keepalive,
                    signal: controller.signal,
                    cache: "no-store"
                };
                if (method !== "GET" && method !== "HEAD") {
                    reqInit.body = options.body;
                }
                let fetchResponseStatus: number;
                // Retry for the following errors:
                // 0: Generic FetchError. See catch block for details of Bug: 3923439.
                // 19: Network error
                // 23: Timeout error
                // 503: Internal server error.
                const FETCH_GENERIC_ERR = 0;
                const retryCodes = [
                    FETCH_GENERIC_ERR,
                    DOMException.NETWORK_ERR,
                    DOMException.TIMEOUT_ERR,
                    503
                ];

                const fetchTimeoutWait = options.timeout
                    ? new CancellableWait(options.timeout)
                    : undefined;
                const promises = [];
                promises.push(fetch(url, reqInit));
                if (fetchTimeoutWait) {
                    promises.push(fetchTimeoutWait.getPromise());
                }
                Promise.race(promises)
                    .then(response => {
                        if (fetchTimeoutWait?.isTimedOut()) {
                            Log.w("{d988e7f}", "{5a69b89}", url);
                            throw {
                                code: DOMException.TIMEOUT_ERR,
                                message: "Timeout while performing HTTP request"
                            };
                        } else if (response instanceof globalThis.Response) {
                            fetchResponseStatus = response.status;
                            return response.text();
                        }
                    })
                    .then(data => {
                        // If server returned 503 with no valid response, then retry
                        if (!data && retryCodes.includes(fetchResponseStatus)) {
                            if (retryCount > 1) {
                                // We have a retryable error. Throw here and retry in catch
                                throw {
                                    code: fetchResponseStatus,
                                    message: "Internal server error."
                                };
                            } else {
                                // We have exhausted all re-tries. Proceed to resolve as the caller will parse the
                                // status codes and convert them to UEC (GsErrorCode/RErrorCode).
                                Log.w("{d988e7f}", "{d639e52}", fetchResponseStatus, max_retry_count);
                            }
                        }
                        resolve({
                            status: fetchResponseStatus,
                            data: data ?? "",
                            retries: max_retry_count - retryCount
                        });
                    })
                    .catch(err => {
                        // Fetch can throw the following DomExceptions:
                        // https://www.w3.org/TR/2012/WD-dom-20120105/#domexception
                        //
                        // However, Fetch can also throw a generic failure: "TypeError: Failed to fetch".
                        // or a non-standard AbortError that extends Error.
                        // https://github.com/nodejs/node/issues/36084#issuecomment-729894622
                        //
                        // Consider all use cases and get an error code for the failure
                        const getFetchErrorCode = (rejectReason: any): number => {
                            let errorCode = FETCH_GENERIC_ERR;
                            if (rejectReason?.code) {
                                errorCode = rejectReason?.code;
                            } else if (rejectReason?.name) {
                                if (rejectReason.name === "AbortError") {
                                    errorCode = DOMException.ABORT_ERR;
                                } else if (rejectReason.name === "NetworkError") {
                                    errorCode = DOMException.NETWORK_ERR;
                                } else if (rejectReason.name === "TimeoutError") {
                                    errorCode = DOMException.TIMEOUT_ERR;
                                }
                            }
                            return errorCode;
                        };
                        // Map the DOMException to UtilsErrorCode
                        const getUtilsErrorCode = (fetchError: number): UtilsErrorCode => {
                            if (
                                [
                                    FETCH_GENERIC_ERR,
                                    DOMException.NETWORK_ERR,
                                    DOMException.TIMEOUT_ERR
                                ].includes(fetchError)
                            ) {
                                Log.d("{d988e7f}", "{0a9fabe}");
                                return UtilsErrorCode.NetworkError;
                            } else if (fetchError == DOMException.ABORT_ERR) {
                                Log.d("{d988e7f}", "{235387a}");
                                return UtilsErrorCode.UserAborted;
                            }
                            Log.w("{d988e7f}", "{f116c88}", fetchError);
                            // Unhandled error. This will end up getting mapped to DOMExceptionInGridServer
                            // If we start seeing a spike in this error code, we will have to look at the logs
                            // and see what other failures are occurring and then add specific handling for them.
                            return UtilsErrorCode.ExceptionHappened;
                        };
                        Log.d("{d988e7f}", "{ec768a1}", JSON.stringify(err), err);

                        // Either a fetch error or when we explicity threw on an internal server error
                        const errCode = getFetchErrorCode(err);
                        if (errCode == FETCH_GENERIC_ERR && options.keepalive) {
                            // Bug: 3923439: https://bugs.chromium.org/p/chromium/issues/detail?id=835821
                            // Preflight request for requests with keepalive specified is not supported in Chrome version 80 and below.
                            // Note that, a failure here does not mean we hit this issue, but there is no other way to determine the
                            // reason for the failure.

                            // WAR: If Fetch fails when keepalive is set to true, we set it to false before retrying.
                            options.keepalive = false;
                        }
                        if (retryCodes.includes(errCode)) {
                            // if the retry is scheduled, returns true
                            if (!retryRequest(errCode)) {
                                // Failing after max_retry_count. If we are here, it could only be for network/fetch errors
                                // For error codes received from server, we should not get into catch, as we check the retry count before throwing
                                Log.w("{d988e7f}", "{dff1a05}", max_retry_count, errCode);
                                reject({
                                    code: getUtilsErrorCode(errCode),
                                    retries: max_retry_count - retryCount
                                });
                            }
                        } else {
                            reject({
                                code: getUtilsErrorCode(errCode),
                                retries: max_retry_count - retryCount
                            });
                        }
                    })
                    .finally(() => {
                        fetchTimeoutWait?.cancel();
                    });
            }
        };
        performRequest();
    });
    return promise;
}

export function IsTouchDevice(): boolean {
    // If the primary mode of pointer input is 'coarse' we are very likely on a mostly-touch or touch-only device.
    // TODO Consider TV systems that use an on-screen pointer, which could easily be 'coarse' as well.
    return window.matchMedia?.("(pointer:coarse)")?.matches;
}

export function IsTouchCapable(): boolean {
    // If any pointer input is 'coarse', then touch is a possible but not a primary pointer input.
    // Do not use this to enable the TouchFriendly app launch mode.
    return !!(window as any)["TouchEvent"] && window.matchMedia?.("(any-pointer:coarse)")?.matches;
}

export function IsTV(platformDetails: PlatformDetails): boolean {
    // TODO Exclude Tizen mobile devices from this check
    return platformDetails.os === PlatformName.TIZEN || platformDetails.os === PlatformName.WEBOS;
}

/**
 * @param platformDetails - interface: PlatformDetails
 * @returns true if this is a mobile device, false if it is not
 **/
export function isMobile(platformDetails: PlatformDetails): boolean {
    return (
        platformDetails.deviceType === DeviceType.PHONE ||
        platformDetails.deviceType === DeviceType.TABLET
    );
}

export function getNewGuid(): string {
    let array = new Uint8Array(36);
    window.crypto.getRandomValues(array);
    let guid = "";

    for (let i = 0; i < 36; i++) {
        let randomNumber = array[i] % 16;
        if (i == 8 || i == 13 || i == 18 || i == 23) {
            guid += "-";
        } else if (i == 14) {
            guid += "4";
        } else if (i == 19) {
            randomNumber = (randomNumber & 0x3) | 0x8;
            guid += randomNumber.toString(16);
        } else {
            guid += randomNumber.toString(16);
        }
    }
    Log.i("{d988e7f}", "{534eaf4}", guid);
    return guid;
}

// Check each part individually to see which is greater
// If they are equal, move on to the next part until we've checked all
// Treat missing parts of the actual version as 0.
// If the actual version has fewer parts than the desired, any of the
// additional parts of the desired version that are non-0 will cause
// a return of false.
function VersionListIsAtLeast(actual: number[], desired: number[]): boolean {
    for (let i = 0; i < desired.length && i < actual.length; ++i) {
        const actualPart = actual[i];
        const desiredPart = desired[i];
        if (actualPart > desiredPart) {
            return true;
        } else if (actualPart < desiredPart) {
            return false;
        }
    }
    if (actual.length < desired.length) {
        for (let i = actual.length; i < desired.length; ++i) {
            if (desired[i] > 0) {
                return false;
            }
        }
    }
    return true;
}

export function IsChromeBrowser(platformDetails: PlatformDetails) {
    return platformDetails.browser == BrowserName.CHROME;
}

/**
 * Compare Chrome browser version to the desired values.
 * Missing values in either the browser version or the desired
 * version will be treated as 0.
 * NOTE: Chrome version information may be incomplete in some
 *       situations - for example, limited to just the major version.
 *       This means that more explcit checks might fail there even
 *       if the browser actually has a higher version.
 *       For example, checking for "at least 84.0.1234.5678" will fail
 *       with a browser that reports only major version 84, even if the
 *       full version is actually 84.0.1400.7800.
 */
export function IsChromeVersionAtLeast(
    platformDetails: PlatformDetails,
    major: number,
    minor?: number,
    build?: number,
    patch?: number
): boolean {
    if (platformDetails.browser != BrowserName.CHROME) {
        return false;
    }
    const desired = [major, minor ?? 0, build ?? 0, patch ?? 0];
    try {
        let browserVer = platformDetails.browserBestVer;

        // The chrome version is split into 4 parts: w.x.y.z
        const actual = browserVer.split(".").map(x => Number.parseInt(x));
        return VersionListIsAtLeast(actual, desired);
    } catch (_ex) {
        Log.w("{d988e7f}", "{bcd8f94}");
    }
    // Return true even if we fail to parse the version. This probably means a newer version of Chrome that
    // changed the version format.
    return true;
}

/**
 * NOTE: Incomplete version information can make "at least" checks return
 *       unexpected failure.
 *       For example, if a browser only provides the major version "84",
 *       when in reality its version is "84.0.5600.7800", then it will
 *       be considered to have version "84.0.0.0" when checking and
 *       not match "84.0.1234.5678" even though it has a higher version.
 */
export function IsSafariVersionAtLeast(
    platformDetails: PlatformDetails,
    major: number,
    minor?: number
): boolean {
    if (platformDetails.browser != BrowserName.SAFARI) {
        return false;
    }

    try {
        return isMajorMinorVersionHelper(platformDetails.browserVer, false, major, minor);
    } catch (_ex) {
        Log.w("{d988e7f}", "{07fd1dd}");
    }
    // Return true even if we fail to parse the version. This probably means a newer version of Safari that
    // changed the version format.
    return true;
}

/**
 * Check iOS/iPadOS browser/OS version details.
 * NOTE: Incomplete version information can make "at least" checks return
 *       unexpected failure.
 *       For example, if a browser only provides the major version "84",
 *       when in reality its version is "84.0.5600.7800", then it will
 *       be considered to have version "84.0.0.0" when checking and
 *       not match "84.0.1234.5678" even though it has a higher version.
 * @param platformDetails - interface: PlatformDetails
 * @param exact - If true, match the exact OS version.  Otherwise, check if OS version is at least the version provided
 * @param major - OS major version
 * @param minor - OS minor version [optional]
 **/
function IsiOSVersionHelper(
    platformDetails: PlatformDetails,
    exact: boolean,
    major: number,
    minor?: number
): boolean {
    if (!IsiDevice(platformDetails)) {
        return false;
    }

    try {
        return isMajorMinorVersionHelper(platformDetails.osVer, exact, major, minor);
    } catch (_ex) {
        Log.w("{d988e7f}", "{cf4149f}");
    }
    // Return true even if we fail to parse the version, unless matching exact version. This probably means a newer version of Safari that
    // changed the version format.
    return exact ? false : true;
}

function isMajorMinorVersionHelper(
    stringVer: string,
    exact: boolean,
    major: number,
    minor?: number
): boolean {
    const numericalVersion = stringVer.replace(/[^0-9.]/, "");
    const actual = numericalVersion.split(".").map(x => Number.parseInt(x));
    const desired = [major, minor ?? 0];
    if (actual.length === 1) {
        actual.push(0);
    }
    if (exact) {
        return actual.every((val, index) => val === desired[index]);
    } else {
        return VersionListIsAtLeast(actual, desired);
    }
}

/**
 * @param platformDetails - interface: PlatformDetails
 * @param major - OS major version
 * @param minor - OS minor version [optional]
 * @returns true if OS version matches the major and minor version provided, false otherwise
 **/
export function IsiOSVersion(
    platformDetails: PlatformDetails,
    major: number,
    minor?: number
): boolean {
    return IsiOSVersionHelper(platformDetails, true, major, minor);
}

/**
 * @param platformDetails - interface: PlatformDetails
 * @param major - OS major version
 * @param minor - OS minor version [optional]
 * @returns true if OS version is at least the major and minor version provided, false otherwise
 **/
export function IsiOSVersionAtLeast(
    platformDetails: PlatformDetails,
    major: number,
    minor?: number
): boolean {
    return IsiOSVersionHelper(platformDetails, false, major, minor);
}

// TODO: Rework the interfaces and use of fetch/XMLHttpRequest to use standard types
//       Rename the Response type used by performRequest() to be more specific
//       Use the standard Response type for customFetch(), and delete the two interfaces below

// interface Body {
//     readonly body: ReadableStream<Uint8Array> | null;
//     readonly bodyUsed: boolean;
//     arrayBuffer(): Promise<ArrayBuffer>;
//     blob(): Promise<Blob>;
//     formData(): Promise<FormData>;
//     json(): Promise<any>;
//     text(): Promise<string>;
// }

export interface FetchResponse {
    //    readonly headers: Headers;
    //    readonly ok: boolean;
    //    readonly redirected: boolean;
    readonly status: number;
    //    readonly statusText: string;
    //    readonly trailer: Promise<Headers>;
    //    readonly type: ResponseType;
    //    readonly url: string;
    //    clone(): FetchResponse;
}

export function customFetch(url: string, timeoutMs = 500, data = {}): Promise<FetchResponse> {
    const controller = new AbortController();
    window.setTimeout(() => {
        controller.abort();
    }, timeoutMs);
    return fetch(url, { ...data, signal: controller.signal })
        .then(response => {
            return response;
        })
        .catch(err => {
            throw err;
        });
}

export function getRErrorDetails(
    code: number,
    description?: string,
    error?: Error | DOMException | null
): ErrorDetails {
    return {
        code,
        description,
        error: error !== null ? error : undefined
    };
}

/**
 * Converts the NetworkType enumeration exposed by Ragnarok and SessionSetup Library to the enums used in telemetry schema.
 * This function can deleted if we maintain consistency between library enums and telemetry schema enums in next schema version.
 * @param network - NetworkType to be converted to telemetry type.
 * @returns NetworkTypeEnum used in telemetry.
 */
export function convertNetworkTypeToTelemetryType(network: NetworkType): NetworkTypeEnum {
    switch (network) {
        case NetworkType.ETHERNET:
            return NetworkTypeEnum.ETHERNET;
        case NetworkType.WIFI: // to be updated once telemetry schemas are changed.
        case NetworkType.WIFI_2_4:
            return NetworkTypeEnum.WIFI_2_4;
        case NetworkType.WIFI_5_0:
            return NetworkTypeEnum.WIFI_5_0;
        case NetworkType.MOBILE:
            return NetworkTypeEnum.MOBILE;
        case NetworkType.MOBILE_2G:
            return NetworkTypeEnum.MOBILE_2G;
        case NetworkType.MOBILE_3G:
            return NetworkTypeEnum.MOBILE_3G;
        case NetworkType.MOBILE_4G:
            return NetworkTypeEnum.MOBILE_4G;
        case NetworkType.MOBILE_5G:
            return NetworkTypeEnum.MOBILE_5G;
        case NetworkType.OTHER:
        case NetworkType.BLUETOOTH:
        case NetworkType.WIMAX:
            return NetworkTypeEnum.OTHER;
        default:
            return NetworkTypeEnum.UNKNOWN;
    }
}
